import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import moment from 'moment';

import CardContainer from 'components/Tvendo/Common/CardContainer';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Button from 'components/CustomButtons/Button';
import LoadingSpinner from 'components/Tvendo/Common/LoadingSpinner';
import { Toasty, useToasty } from 'hooks/Toasty';
import { wsDownload, wsGetAll } from 'services/WService';
import { WithApp } from 'contexts/appContext';

const useStyles = makeStyles(() => ({
  ayuda: {
    color: '#999',
    fontSize: '0.85rem',
    marginTop: '10px',
  },
  corte: {
    color: '#555',
    fontSize: '0.9rem',
    marginTop: '4px',
  },
  pesado: {
    color: '#b26500',
    fontSize: '0.9rem',
    marginTop: '4px',
  },
  vacio: {
    color: '#b26500',
    fontSize: '0.9rem',
  },
}));

const TODAS = '%';
//Opcion del combo de mesa: todas las mesas ARCOR de la zona elegida.
const ARCOR = 'ARCOR';

//Medido con datos reales de 202608: para estimar el peso y la espera antes de descargar.
const BYTES_POR_FILA = 264;
const MS_POR_FILA = 0.241;

//Desde aca se avisa: el back aguanta, pero a Excel le cuesta abrir el archivo.
const FILAS_PESADO = 150000;

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Setiembre', 'Octubre', 'Noviembre', 'Diciembre'];

//Sin moment: el locale espanol no siempre esta cargado en el portal.
function nombrePeriodo(periodo) {
  const texto = String(periodo);
  const mes = MESES[parseInt(texto.substring(4, 6), 10) - 1];
  return mes ? `${mes} ${texto.substring(0, 4)}` : texto;
}

function SalesExcel() {
  const classes = useStyles();
  const [notify, showNotify] = useToasty();

  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState('');
  const [zone, setZone] = useState(TODAS);
  const [zones, setZones] = useState([]);
  const [mesa, setMesa] = useState(TODAS);
  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  async function listZones() {
    let response = await wsGetAll('base/zone');
    if (response.success) setZones(response.data);
  }

  async function listPeriods() {
    let response = await wsGetAll('report/sales-excel/periodos');
    if (response.success) {
      setPeriods(response.data);
      //Solo las mesas que puede ver el usuario: las suyas si es supervisor, si no las de sus zonas.
      setMesas(response.mesas || []);
      //El mas reciente ya viene primero: el back ordena por periodo DESC.
      if (response.data.length > 0) setPeriod(response.data[0].periodo);
    } else {
      //Ej. "El usuario no tiene zonas asignadas", para que no salga "Todavia no hay datos".
      setError(response.message || 'No se pudieron cargar los periodos');
    }
    setLoading(false);
  }

  useEffect(() => {
    listZones();
    listPeriods();
  }, []);

  const seleccionado = periods.find((item) => item.periodo === period);

  //Las mesas de la zona elegida (con todas las zonas, todas las del usuario).
  const mesasZona = mesas.filter((item) => zone === TODAS || item.zona === zone);
  const mesasArcorZona = mesasZona.filter((item) => (item.description || '').includes('ARCOR'));

  //Codigos de mesa que se van a pedir. Vacio = todas las mesas.
  let codigosMesa = [];
  if (mesa === ARCOR) codigosMesa = mesasArcorZona.map((item) => item.code);
  else if (mesa !== TODAS) codigosMesa = [mesa];

  //Filas de la descarga, sumadas del detalle por zona y mesa que manda el back.
  let filas = 0;
  for (const z of seleccionado?.zonas || []) {
    if (zone !== TODAS && z.zona !== zone) continue;
    for (const m of z.mesas) {
      if (codigosMesa.length === 0 || codigosMesa.includes(m.mesa)) filas += m.filas;
    }
  }

  const mb = (filas * BYTES_POR_FILA) / 1048576;
  const seg = (filas * MS_POR_FILA) / 1000;
  const tiempo = seg < 60
    ? `${Math.round(seg)} segundos`
    : `${Math.round(seg / 60)} minuto${seg >= 90 ? 's' : ''}`;

  async function descargar() {
    const params = new URLSearchParams({ periodo: period });
    if (zone !== TODAS) params.append('zone_code', zone);
    if (codigosMesa.length > 0) params.append('mesa_codes', codigosMesa.join(','));

    setProcessing(true);
    try {
      let sufijo = zone === TODAS ? '' : `_${zone}`;
      if (mesa === ARCOR) sufijo += '_ARCOR';
      else if (mesa !== TODAS) sufijo += `_mesa${mesa}`;
      await wsDownload(`report/sales-excel?${params.toString()}`, `ventas_${period}${sufijo}.xlsx`);
    } catch (ex) {
      //404 si no hay ventas, 403 si no tiene acceso; wsDownload deja el json en response.data.
      showNotify(ex?.response?.data?.message || 'No se pudo generar el archivo', 'error');
    } finally {
      setProcessing(false);
    }
  }

  return (
    <GridContainer>
      <Toasty action={notify} />
      <GridItem xs={12}>
        <CardContainer
          title={(
            <>
              Descarga de ventas {(processing || loading) && <LoadingSpinner />}
            </>
          )}
        >
          <GridContainer>
            <GridItem xs={12} sm={4} md={3}>
              <FormControl fullWidth margin="normal">
                <InputLabel shrink>Periodo</InputLabel>
                <Select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  disabled={processing || loading || periods.length === 0}
                  displayEmpty
                >
                  {periods.map((item) => (
                    <MenuItem key={item.periodo} value={item.periodo}>
                      {nombrePeriodo(item.periodo)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem xs={12} sm={4} md={3}>
              <FormControl fullWidth margin="normal">
                <InputLabel shrink>Zona</InputLabel>
                <Select
                  value={zone}
                  onChange={(e) => {
                    setZone(e.target.value);
                    setMesa(TODAS);
                  }}
                  disabled={processing}
                >
                  <MenuItem value={TODAS}>Todas las zonas</MenuItem>
                  {zones.map((item) => (
                    <MenuItem key={item.id} value={item.code}>
                      {item.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem xs={12} sm={4} md={3}>
              <FormControl fullWidth margin="normal">
                <InputLabel shrink>Mesa</InputLabel>
                <Select
                  value={mesa}
                  onChange={(e) => setMesa(e.target.value)}
                  disabled={processing}
                >
                  <MenuItem value={TODAS}>Todas las mesas</MenuItem>
                  {/* Sin mesas ARCOR en la zona no se ofrece: bajaria todas las mesas */}
                  {mesasArcorZona.length > 0 && <MenuItem value={ARCOR}>Mesas ARCOR</MenuItem>}
                  {mesasZona.map((item) => (
                    <MenuItem key={item.code} value={item.code}>
                      {item.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            <GridItem xs={12} sm={4} md={3} style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                color="success"
                onClick={descargar}
                disabled={processing || loading || period === '' || filas === 0}
              >
                {processing ? 'Generando...' : 'Descargar Excel'}
              </Button>
            </GridItem>

            <GridItem xs={12}>
              {!loading && error !== '' && <div className={classes.vacio}>{error}</div>}

              {!loading && error === '' && periods.length === 0 && (
                <div className={classes.vacio}>
                  Todavía no hay datos cargados. La información la deja un proceso
                  programado; si esto sigue así, avisa a sistemas.
                </div>
              )}

              {seleccionado && (
                <div className={classes.corte}>
                  {filas.toLocaleString('es-PE')} registros
                  {' '}&nbsp;·&nbsp;{' '}
                  aprox. {mb.toLocaleString('es-PE', { maximumFractionDigits: 1 })} MB
                  {' '}&nbsp;·&nbsp;{' '}
                  {tiempo}
                  {seleccionado.cargado_at && (
                    <> &nbsp;·&nbsp; datos al {moment(seleccionado.cargado_at).format('DD/MM/YYYY HH:mm')}</>
                  )}
                </div>
              )}

              {filas >= FILAS_PESADO && (
                <div className={classes.pesado}>
                  Es un archivo grande. Se descarga sin problema, pero a Excel le va a
                  costar abrirlo: eligiendo una zona baja bastante.
                </div>
              )}

              <div className={classes.ayuda}>
                La información la deja un proceso programado, así que puede no incluir los
                últimos días. El archivo se arma mientras se descarga: no cierres la
                pestaña hasta que termine. Se baja en Excel, con los números como números
                y las fechas como fechas.
              </div>
            </GridItem>
          </GridContainer>
        </CardContainer>
      </GridItem>
    </GridContainer>
  );
}

export default WithApp(SalesExcel);
